import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Sun, Cloud, ArrowRight, Zap } from 'lucide-react';

const UniversityOfFlorida: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "University of Florida";

    return (
        <div className="min-h-screen bg-white font-sans text-[#0021A5] selection:bg-[#FA4616] selection:text-white">
            {/* Utility */}
            <div className="bg-[#f2f2f2] text-[#333] py-2 px-6 flex justify-between items-center text-xs font-bold uppercase tracking-wide">
                 <div className="flex gap-4">
                     <span className="flex items-center gap-1 text-[#FA4616]"><Sun size={12}/> Gainesville, FL 82°F</span>
                 </div>
                 <div className="flex gap-6">
                    <a href="#" className="hover:text-[#0021A5]">Directory</a>
                    <a href="#" className="hover:text-[#0021A5]">News</a>
                    <a href="#" className="hover:text-[#0021A5]">Calendar</a>
                    <a href="#" className="hover:text-[#0021A5]">GatorMail</a>
                 </div>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-100">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                         <div className="bg-[#0021A5] text-white font-serif font-black text-2xl w-10 h-10 flex items-center justify-center rounded-sm">{schoolName.split(' ')[0][0]}{schoolName.split(' ')[1]?.[0]}</div>
                         <span className="font-serif text-2xl font-bold text-[#0021A5] uppercase tracking-wide">{schoolName}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#0021A5] uppercase text-sm tracking-wide">
                        <a href="#" className="hover:text-[#FA4616] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#FA4616] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#FA4616] transition-colors">Campus Life</a>
                        <a href="#" className="hover:text-[#FA4616] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#FA4616] transition-colors">About</a>
                    </div>
                    
                    <div className="flex gap-4">
                         <div className="bg-[#FA4616] text-white p-2 rounded-full cursor-pointer hover:bg-[#d63a10] transition-colors">
                            <Search size={18} />
                         </div>
                         <Menu className="text-[#0021A5] cursor-pointer lg:hidden" size={28} />
                    </div>
                </div>
            </nav>

            {/* Hero - Gator Nation */}
            <header className="relative h-[650px]">
                 <img 
                    src="https://images.unsplash.com/photo-1563805909930-4e3305d2c0b6?q=80&w=2070&auto=format&fit=crop" 
                    alt="Swamp" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0021A5]/80 via-[#0021A5]/30 to-transparent"></div>

                <div className="absolute bottom-0 left-0 w-full p-12 md:p-20">
                     <div className="max-w-2xl bg-white/90 backdrop-blur-sm p-10 border-l-8 border-[#FA4616]">
                         <h1 className="text-5xl md:text-7xl font-sans font-black text-[#0021A5] mb-6 uppercase italic">
                             Go Greater.
                         </h1>
                         <p className="text-xl text-[#333] mb-8 font-medium">
                             The Gator Nation pushes boundaries, challenges convention, and shapes the future.
                         </p>
                         <Button className="bg-[#0021A5] hover:bg-[#00187a] text-white font-bold uppercase tracking-widest px-8 h-12 rounded-sm border-2 border-transparent">
                             Apply Now
                         </Button>
                     </div>
                </div>
            </header>

            {/* Power Stats */}
            <section className="bg-[#0021A5] text-white py-20 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
                     <div>
                         <Zap size={40} className="text-[#FA4616] mx-auto mb-4" />
                         <h3 className="text-4xl font-black italic">TOP 5</h3>
                         <p className="font-bold uppercase text-xs tracking-widest">Public University (Wall Street Journal)</p>
                     </div>
                      <div>
                         <h3 className="text-4xl font-black italic text-[#FA4616] mt-9">$1.25B</h3>
                         <p className="font-bold uppercase text-xs tracking-widest">Research Expenditures</p>
                     </div>
                      <div>
                         <h3 className="text-4xl font-black italic text-[#FA4616] mt-9">500k+</h3>
                         <p className="font-bold uppercase text-xs tracking-widest">Alumni Worldwide</p>
                     </div>
                      <div>
                         <h3 className="text-4xl font-black italic text-[#FA4616] mt-9">16</h3>
                         <p className="font-bold uppercase text-xs tracking-widest">Colleges</p>
                     </div>
                 </div>
            </section>

             {/* News Blocks */}
             <section className="bg-white py-24 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto">
                     <h2 className="text-4xl font-black text-[#0021A5] italic uppercase mb-12">Gator News</h2>

                     <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                         {[
                             { title: `${schoolName} Health performs first-of-its-kind heart transplant`, tag: "Health", img: "https://images.unsplash.com/photo-1551076805-e1869033e561?q=80&w=2070&auto=format&fit=crop" },
                             { title: `${schoolName.split(' ').pop()} Museum opens fascinating new exhibit on prehistoric life`, tag: "Museum", img: "https://images.unsplash.com/photo-1518385732280-92262d47e4c2?q=80&w=2070&auto=format&fit=crop" },
                             { title: "Researchers harness AI to predict hurricane paths with greater accuracy", tag: "Innovation", img: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070&auto=format&fit=crop" }
                         ].map((item, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <div className="relative h-64 mb-4 overflow-hidden rounded-sm">
                                     <img src={item.img} className="w-full h-full object-cover transition-transform group-hover:scale-105" alt={item.title}/>
                                     <span className="absolute bottom-0 left-0 bg-[#FA4616] text-white text-xs font-bold uppercase px-3 py-1">{item.tag}</span>
                                 </div>
                                 <h3 className="text-xl font-bold text-[#0021A5] leading-tight group-hover:underline decoration-[#FA4616] decoration-2 underline-offset-4">{item.title}</h3>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#111] text-white py-16 px-6 md:px-12 border-t-8 border-[#0021A5]">
                  <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                          <span className="font-serif font-black text-3xl text-[#FA4616] uppercase mb-1 block">{schoolName.split(' ')[0]}</span>
                          <span className="font-serif font-bold text-xl uppercase mb-6 block">{schoolName}</span>
                          <p className="text-gray-400 mb-6">
                              Gainesville, FL 32611 <br/>
                              (352) 392-3261
                          </p>
                      </div>

                      <div className="grid grid-cols-2 gap-12 font-bold text-gray-400 uppercase text-xs tracking-widest">
                          <ul className="space-y-4">
                              <li><a href="#" className="hover:text-[#FA4616]">Website Listing</a></li>
                              <li><a href="#" className="hover:text-[#FA4616]">Accessibility</a></li>
                              <li><a href="#" className="hover:text-[#FA4616]">Security</a></li>
                          </ul>
                          <ul className="space-y-4">
                              <li><a href="#" className="hover:text-[#FA4616]">Regulations</a></li>
                              <li><a href="#" className="hover:text-[#FA4616]">Privacy</a></li>
                              <li><a href="#" className="hover:text-[#FA4616]">Student Jobs</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-500">
                      © 2026 {schoolName}
                  </div>
             </footer>
        </div>
    );
};

export default UniversityOfFlorida;
